import { useEffect, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';

import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import ProfileSection from 'app/components/UI/ProfileSection';

import DefaultValuesSection from './DefaultValuesSection';
import SavingsCategoriesSection from './SavingsCategoriesSection';

type SystemSettingsTab = 'recognition-defaults' | 'savings';

interface TabConfig {
  label: SystemSettingsTab;
  displayName: string;
}

// The page is the home for global/system-wide settings; add new areas as tabs here.
const TABS: TabConfig[] = [
  { label: 'recognition-defaults', displayName: 'Recognition defaults' },
  { label: 'savings', displayName: 'Savings' },
];

function SystemSettings() {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentTabLabel = useMemo<SystemSettingsTab>(() => {
    const tabParam = searchParams.get('tab');
    const isValid = tabParam && TABS.some((tab) => tab.label === tabParam);
    return isValid ? (tabParam as SystemSettingsTab) : TABS[0].label;
  }, [searchParams]);

  const [tabLabel, setTabLabel] = useState<SystemSettingsTab>(currentTabLabel);

  useEffect(() => {
    setTabLabel(currentTabLabel);
  }, [currentTabLabel]);

  const handleTabsChange = (index: number) => {
    const newTabLabel = TABS[index].label;
    setTabLabel(newTabLabel);
    setSearchParams({ tab: newTabLabel });
  };

  const renderTabContent = (tab: TabConfig) => {
    if (tab.label === 'recognition-defaults') {
      return (
        <ProfileSection title="Default company values" isEditMode maxW="100%">
          <DefaultValuesSection />
        </ProfileSection>
      );
    }

    if (tab.label === 'savings') {
      return (
        <ProfileSection title="Savings categories" isEditMode maxW="100%">
          <SavingsCategoriesSection />
        </ProfileSection>
      );
    }

    return null;
  };

  return (
    <AuthenticatedPageWrapper title="System settings">
      <Tabs
        w="full"
        isLazy
        index={TABS.findIndex((tab) => tab.label === tabLabel)}
        onChange={handleTabsChange}
      >
        <TabList>
          {TABS.map((tab) => (
            <Tab key={tab.label}>{tab.displayName}</Tab>
          ))}
        </TabList>

        <TabPanels mt="24px">
          {TABS.map((tab) => (
            <TabPanel key={tab.label} w="full" px="0">
              {renderTabContent(tab)}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </AuthenticatedPageWrapper>
  );
}

export default SystemSettings;
